import {parseDateWithFormat} from '@shared/dateUtils';
import {useSuspenseQueries} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import {ApiResponseGetRecallInfoResponse, Inspection} from '@di-shared/api/di/__generated';
import environmentStore from '@di-shared/features/environment/environmentStore';

const LAST_ROUND = 8 as const;

export enum InspectionStatus {
  COMPLETE = 'COMPLETE',
  EXPIRED = 'EXPIRED',
  READY = 'READY',
  INVALID = 'INVALID',
}

export enum MaintenanceGrade {
  S = 'S',
  A = 'A',
  B = 'B',
  E = 'E',
}

export enum AdditionalTextCase {
  NORMAL,
  TRANSFERRED,
  EXPIRED,
}

export enum RecallInfoCase {
  HAS_RECALL_INFO,
  NO_RECALL_INFO,
}

/**
 * @link https://hmg.dooray.com/project/tasks/3903671603620760176#comment-3908886481642006987
 */
function useMaintenanceGrade(vin: string) {
  const puid = environmentStore.getState().puid;
  const [cpoGrade, recallInfoList, inspectionList] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApi.cpo.getCpoGrade), vin, puid],
        queryFn: () => diApi.cpo.getCpoGrade({vin, puid}),
      },
      {
        queryKey: [getQueryKey(diApi.cpo.getRecallInfoList1), vin],
        queryFn: () => diApi.cpo.getRecallInfoList1({vin}),
        select: (data: ApiResponseGetRecallInfoResponse) =>
          data.data.recallInfos.map(({enfcCdt, priInsNm}) => ({
            occurrenceDate: parseDateWithFormat(enfcCdt),
            inspectionDetail: priInsNm,
          })),
      },
      {
        queryKey: [getQueryKey(diApi.cpo.getInspectionList1), vin],
        queryFn: () => diApi.cpo.getInspectionList1({vin}),
      },
    ],
  });

  /* 첫번째 swiper */
  const {maintenanceGrade} = cpoGrade.data.data;
  const {data: recallInfos} = recallInfoList;
  const {inspections, nowRound} = inspectionList.data.data;

  const recallInfoCase = getRecallInfoCase(recallInfos);

  /* 두번째 swiper */
  const currentRoundEndDate = getCurrentRoundEndDate(nowRound, inspections);
  const isButtonEnabled = getIsButtonEnabled(inspections);
  const additionalTextCase = getAdditionalTextCase(inspections);

  return {
    maintenanceGrade,
    recallInfos,
    recallInfoCase,

    currentRoundEndDate,
    isButtonEnabled,
    additionalTextCase,
    inspections,
  };
}

export default useMaintenanceGrade;

function getAdditionalTextCase(inspections: Inspection[]) {
  /**
   * 이월 case와 만료 case가 겹칠 경우 -> 이월text 표출
   */
  if (getIsExpiredMaintenanceCycle(inspections)) {
    return AdditionalTextCase.TRANSFERRED;
  }
  if (getIsMaintenanceSkipped(inspections)) {
    return AdditionalTextCase.EXPIRED;
  }
  return AdditionalTextCase.NORMAL;
}

function getIsMaintenanceSkipped(inspections: Inspection[]) {
  return inspections.some(({status}) => status === InspectionStatus.EXPIRED);
}

function getIsExpiredMaintenanceCycle(inspections: Inspection[]) {
  return inspections.some(({isTransfer}) => isTransfer);
}

function getIsPeriodExpired(inspections: Inspection[]) {
  const today = new Date();
  const expiredDate = parseDateWithFormat(inspections.find(({round}) => round === LAST_ROUND)?.endDate);
  if (expiredDate) {
    return today > expiredDate;
  }
  console.error('invalid endDate', inspections);
  return false;
}

function getIsButtonEnabled(inspections: Inspection[]) {
  /**
   * 버튼 활성화 기준
   * 정기점검 회차중 READY 상태인 회차가 1회차 이상 있는 경우
   * AND
   * 현재일이 8회차 만료일 이전인 경우 -> 만료가 안된 경우
   *
   * 버튼 비활성화 기준
   * 정기점검 회차 중 READY 상태 회차가 없는경우
   * OR
   * 현재일이 8회차 만료일 이후인 경우
   */
  const hasReadyStatus = inspections.some(({status}) => status === InspectionStatus.READY);
  const isPeriodExpired = getIsPeriodExpired(inspections);
  return hasReadyStatus && !isPeriodExpired;
}

function getCurrentRoundEndDate(currentRound: number, inspections: Inspection[]) {
  return parseDateWithFormat(inspections.find(({round}: any) => currentRound === round)?.endDate);
}

function getRecallInfoCase(recallInfos: {occurrenceDate: Date | null; inspectionDetail: string}[]) {
  if (recallInfos.length > 0) {
    return RecallInfoCase.HAS_RECALL_INFO;
  }
  return RecallInfoCase.NO_RECALL_INFO;
}
